/**
 * The `settings` namespace, in English — B3, the studio's own settings (`KLQ-20260819-008`).
 *
 * Its own namespace rather than more of `studio`, per the rule in `lib/i18n/catalog.ts`:
 * namespaces split by screen family, and B2 answers *who works here* while B3 answers *what
 * this firm is*. They also have different authorities, which is why they are different URLs.
 *
 * **No tier name appears in this file, and none may be added.** `new`, `verified`, `trusted`
 * and `restricted` are internal rungs of `core/trust.py`; printing one teaches a customer a
 * vocabulary whose only use is feeling judged. What the firm needs to know is what it can do,
 * so `trust.consequence.*` says that instead — three sentences derived from the same number
 * nobody sees (`KLQ-20260819-008` §4).
 *
 * **The restriction reason is not here either, and that is deliberate.** It is written by a
 * human operator, per incident, and is rendered verbatim from `trust_restricted_reason`.
 * `KLQ-20260814-004` §C is explicit that there is no shadow banning: if the restriction was a
 * mistake, the customer is the only person positioned to say so, and a paraphrase would take
 * that away.
 *
 * LENGTH: `scripts/check-expansion.mjs` pseudo-expands every message here by 1.3×. Keys
 * ending in `.title` are measured against the 3-line heading slot; everything else against
 * the wrapping-prose budget. A message that trips it is shortened here, never given more
 * room there.
 */

export const settingsEn = {
  'page.title': 'Studio settings',
  'page.subtitle': 'What your firm is called, where it works, and which domains it owns.',

  'status.loading': 'Loading your studio…',

  // A plain member who typed the URL. The rail hides the entry, so this is the second half of
  // the same rule rather than its only enforcement — and the server refuses independently.
  'notPermitted.explains': 'Studio settings apply to every project: domains, task types, email delivery.',
  'notPermitted.title': 'Only admins and managers',
  'notPermitted.body':
    'Your studio’s settings can be changed by an admin or a manager. Ask one of them if something here needs to change.',

  // A session whose home workspace is unclaimed — an invited collaborator from another firm,
  // typically. `MCK-20260812-009` §E gives them no chrome; this is the same rule for a typed
  // URL, and it says what is true rather than accusing them of lacking a permission.
  'noStudio.explains': 'A studio is your company in Kalqa: its people, projects and settings.',
  'noStudio.title': 'No studio yet',
  'noStudio.body':
    'You have not set up a company of your own. These settings appear once you do. Your projects and files are unaffected.',

  // --- The restriction notice ------------------------------------------------
  'restriction.title': 'Sending is restricted',
  'restriction.intro': 'We have limited what this studio can send. The reason recorded was:',
  'restriction.help':
    'If that looks wrong, reply to any message from us and a person will read it. Your projects, files, history and export are untouched.',

  // --- What the studio may currently do --------------------------------------
  'trust.consequence.allowed': 'Your studio can invite people by email.',
  'trust.consequence.blocked':
    'Confirm your own email address, or verify a domain below, to invite people by email.',
  'trust.consequence.restricted':
    'Your studio cannot email people at the moment. Everything else works as usual.',
  'trust.action.confirmAddress': 'Send me a confirmation link',
  'trust.confirmSent': 'On its way. Open the link in that email to confirm your address.',
  'trust.confirmFailed': 'That confirmation email could not be sent. Try again in a moment.',

  // --- Identity ---------------------------------------------------------------
  'identity.title': 'Company',
  'identity.subtitle': 'What colleagues and clients see on drawings and in invitations.',

  'field.name.label': 'Company name',
  'field.name.placeholder': 'Novák Statika',
  // TWIN: `field.slug.label` in `./setup.ts` names the same field before it is chosen, and the
  // two have to agree — a person who typed it under one name should recognise it under the
  // other. §6.6 would permit "workspace" on this screen alone; saying it here and nowhere else
  // would just be the one place the word survives.
  'field.slug.label': 'Address in Kalqa',
  // TWIN: `field.slug.hint` in `./setup.ts` says the same rule *before* the choice is made.
  // The two exist separately because a language pack can ship one namespace without the
  // other; if the rule changes, it changes in both files (retro rule 12). The rule since
  // `KLQ-20261023-009` (owner 2026-10-28): changeable until the first shared link uses it —
  // `hintChangeable` while the server says `slug_changeable`, `hint` once it no longer does.
  'field.slug.hintChangeable':
    'Lower-case letters, digits and single hyphens. You can change it until the first shared link uses it.',
  'field.slug.hint': 'A shared link already uses this address, so it can no longer change.',
  'field.country.label': 'Country',
  'field.country.hint': 'Seeds drawing numbers, paper sizes and norms. Each project can differ.',
  'field.locale.label': 'Company language',
  'field.locale.hint':
    'Used for documents, generated mail, invitations and the interface everyone in the studio sees.',
  'field.norm.label': 'Norm preset',

  'action.save': 'Save changes',
  'action.saved': 'Saved.',
  'action.discard': 'Discard',

  'error.nameRequired': 'Enter the name of your company.',
  // TWIN: `error.slugShort` / `error.slugLong` / `error.slugPattern` in `./setup.ts`, the same
  // limits checked before the address is first chosen.
  'error.slugShape': 'Use {min} to {max} lower-case letters, digits and single hyphens.',
  // TWIN: `error.slugTaken` in `./setup.ts` — the same collision, the same sentence.
  'error.slugTaken': 'That address is taken. Choose another one.',
  'error.slugLocked': 'A shared link started using this address in the meantime, so it can no longer change.',

  // --- The 412 conflict --------------------------------------------------------
  'conflict.title': 'Somebody else edited this',
  'conflict.body':
    'These settings changed while you had them open. Nothing you typed has been sent. Pick which version to keep.',
  'conflict.yours': 'Yours',
  'conflict.theirs': 'Now saved',
  'conflict.action.useTheirs': 'Use the saved version',
  'conflict.action.keepMine': 'Save mine instead',

  // --- Domains -------------------------------------------------------------------
  'domains.title': 'Domains',
  'domains.subtitle':
    'Prove your firm owns a domain and colleagues on it can join without an invitation.',
  'domains.explains': 'A verified domain lets colleagues with an address on it join without an invitation.',
  'domains.empty.title': 'No domain yet',
  'domains.empty': 'No domain claimed yet.',

  'domain.field.label': 'Domain',
  'domain.field.placeholder': 'studio.example',
  'domain.action.claim': 'Verify domain',

  'domain.state.verified': 'Verified {date}',
  'domain.state.unverified': 'Not verified',
  'domain.checked': 'Last checked {time}',
  'domain.neverChecked': 'Not checked yet',
  'domain.notFound': 'No record found yet. DNS changes can take a few minutes to spread.',
  'domain.checking': 'Looking for the record…',

  'domain.record.title': 'Publish this record',
  'domain.record.names': 'Add a TXT record under either of these names:',
  'domain.record.value': 'Value',
  'domain.record.keep': 'Keep the record in place. We re-check it from time to time.',

  'domain.action.check': 'Check now',
  'domain.action.copy': 'Copy',
  'domain.action.copied': 'Copied',
  'domain.action.remove': 'Remove',
  'domain.remove.title': 'Remove this domain?',
  'domain.remove.body':
    'Colleagues on it will no longer join automatically. If this was the only thing your studio has proved, it will be able to send fewer emails.',
  'domain.remove.confirm': 'Remove it',
  'domain.remove.cancel': 'Keep it',

  'domain.error.notADomain': 'Enter a domain like studio.example. Not an email address, and no https://.',
  'domain.error.notCompany':
    'That is a consumer or throwaway email provider, not a company domain. Nobody can publish a record on one.',
  'domain.error.claimedElsewhere':
    'Another studio has already proved it owns that domain. One domain belongs to one studio. If that is wrong, contact support.',

  // --- Task types (`KLQ-20260825-005`) ----------------------------------------------
  //
  // The words a studio uses for the work it does, defined once and picked from when tasks are
  // generated. `taskType.action.delete` and `taskType.action.archive` are two labels for one
  // button: the server deletes a type nothing has used and archives one a task names, and the
  // row's `deletable` says which is coming before it is pressed.
  'taskTypes.title': 'Task types',
  'taskTypes.subtitle':
    'What a task can be here. Generating work picks from this list, so a title written once is a title every project agrees on.',
  'taskTypes.explains': 'A task type names a kind of work once, for every project in the studio.',
  'taskTypes.empty.title': 'No task types',
  'taskTypes.empty': 'No task types yet. Add the first one and generating work can use it.',

  'taskType.field.name': 'Name',
  'taskType.field.template': 'Title',
  // `{label}` is an ICU argument here whose value is the literal string `{label}`
  // (`TaskTypesSection.tsx`, `TOKENS`). It is the server's placeholder, typed by a person into
  // a title template — keep it in the sentence, do not translate it, and do not add an
  // argument of the same name.
  'taskType.field.templateHint':
    'Written on every task raised from this type. {label} becomes the name of the unit, floor or drawing.',
  'taskType.field.icon': 'Icon',
  'taskType.field.color': 'Colour',
  'taskType.field.kind': 'Kind',
  'taskType.field.priority': 'Priority',
  'taskType.field.verification': 'Someone else has to confirm the work is done',

  'taskType.kind.task': 'Task',
  'taskType.kind.issue': 'Issue',
  'taskType.kind.question': 'Question',
  'taskType.priority.low': 'Low',
  'taskType.priority.normal': 'Normal',
  'taskType.priority.high': 'High',

  'taskType.badge.verification': 'Needs confirmation',
  'taskType.badge.archived': 'Archived',

  'taskType.action.add': 'Add a task type',
  'taskType.action.edit': 'Edit',
  'taskType.action.save': 'Save',
  'taskType.action.cancel': 'Cancel',
  'taskType.action.up': 'Up',
  'taskType.action.down': 'Down',
  'taskType.action.archive': 'Archive',
  'taskType.action.restore': 'Restore',
  'taskType.action.delete': 'Delete',

  // --- Failures ---------------------------------------------------------------------
  // --- Undeliverable addresses (issue 165) -------------------------------------------
  'suppressions.title': 'Undeliverable addresses',
  'suppressions.subtitle':
    'Colleagues and invitees Kalqa has stopped emailing because the mail bounced or was reported. Once a mailbox works again, ask to re-enable it. Kalqa support reviews every request.',
  'suppressions.explains': 'Addresses Kalqa no longer emails because messages bounced or were reported.',
  'suppressions.empty.title': 'Every address is reachable',
  'suppressions.empty': 'Nobody in this studio is on the list.',
  'suppressions.column.address': 'Address',
  'suppressions.column.reason': 'Reason',
  'suppressions.column.stream': 'Stopped',
  'suppressions.column.since': 'Since',
  'suppressions.column.request': 'Re-enable',
  'suppressions.error.load': 'The undeliverable addresses could not be loaded.',
  'suppressions.more': 'Show more',
  'suppression.reason.hardBounce': 'Mailbox does not exist',
  'suppression.reason.complaint': 'Reported as spam',
  'suppression.reason.manual': 'Stopped by support',
  'suppression.reason.unsubscribe': 'Unsubscribed',
  'suppression.stream.auth': 'Sign-in mail',
  'suppression.stream.notify': 'Notifications',
  'suppression.stream.all': 'All mail',
  'suppression.action.request': 'Ask to re-enable',
  'suppression.requested': 'Requested {date}',
  'suppression.error.gone': 'This address is no longer blocked.',

  'error.load': 'Your studio’s settings could not be loaded.',
  'error.forbidden': 'You do not have permission to change this.',
  'error.gone': 'That domain is no longer here.',
  'error.rateLimited': 'Too many attempts. Wait a moment and try again.',
  'error.validation': 'Something in that request was not accepted.',
  'error.unexpected': 'Something went wrong. Try again.',
} as const;

export type SettingsMessageKey = keyof typeof settingsEn;
